import React, { useState, useRef, useEffect } from 'react';

interface TooltipData {
  deposit: number;
  claimable: number;
  total: number;
}

interface InfoIconProps {
  onClick: () => void;
  title?: string;
  tooltipData?: TooltipData;
}

export const InfoIcon: React.FC<InfoIconProps> = ({ onClick, title = "Click for details", tooltipData }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTooltip(true);
    onClick();
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Hide tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (iconRef.current && !iconRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  return (
    <div
      ref={iconRef}
      className="info-icon"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setShowTooltip(true);
          onClick();
        }
      }}
    >
      <span className="info-icon-text">i</span>
      {showTooltip && (
        <div className="info-tooltip">
          {tooltipData ? (
            <div className="tooltip-table">
              <div className="tooltip-row">
                <span className="tooltip-label">Deposit</span>
                <span className="tooltip-value">{formatCurrency(tooltipData.deposit)}</span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">Claimable</span>
                <span className="tooltip-value">{formatCurrency(tooltipData.claimable)}</span>
              </div>
              <div className="tooltip-row tooltip-total">
                <span className="tooltip-label">Total</span>
                <span className="tooltip-value">{formatCurrency(tooltipData.total)}</span>
              </div>
            </div>
          ) : (
            title
          )}
        </div>
      )}
    </div>
  );
};
